/**
 * ============================================================================
 * METRICS API — RTK Query endpoint for getMetrics
 * ============================================================================
 *
 * CACHE:
 * - getMetricsWeek: ONE reporting period per entry, key = {id, dataMode,
 *   reportingPeriod} — the unit useMetricsWindow assembles windows from.
 * - getMetrics: whole-window entry, key = {dashboardId, reviewType,
 *   reportingPeriods, dataMode} (paginationKey excluded).
 * - refetchOnFocus: false — user controls when to refresh; no auto-refetch on tab focus.
 * - Future: include lastRefreshedAt in args (polled via /refresh-status) so
 *   cache key rotates automatically when backend MV refreshes.
 *
 * PAGINATION: owned by the accessor (GetMetricsApi.getMetricsAllPages), which
 * follows paginationKey and returns the fully accumulated response.
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { GetMetricsApi } from 'src/accessors/GetMetricsApi';
import { ReportingApiError } from 'src/accessors/ReportingApiClient';
import { WBR_REVIEW_TYPE } from 'src/constants';
import { DataMode, GetMetricsInput, GetMetricsOutput } from 'src/types/metricsApi.types';

export type { GetMetricsInput, GetMetricsOutput, DcrSnapshotData } from 'src/types/metricsApi.types';
export type { MetricEntry, DcrRecord } from 'src/types/metricsApi.types';

// ──── RTK Query (the cache-policy layer; transport lives in the accessor) ────

/**
 * One reporting period's worth of metrics: the unit of caching. `id` is the
 * client-side dashboard identity (WBR_DASHBOARD_ID); the queryFn maps it to
 * the wire's dashboardId + reviewType. reviewType joins this key when
 * multi-review selection ships.
 */
export interface GetMetricsWeekArgs {
  id: string;
  dataMode: DataMode;
  reportingPeriod: string;
}

export const metricsApi = createApi({
  reducerPath: 'metricsApi',
  baseQuery: fakeBaseQuery<{ status: number; data: unknown }>(), // all endpoints use queryFn → accessor
  keepUnusedDataFor: Infinity, // Cache lives forever — only invalidated on explicit user refresh
  refetchOnFocus: false,       // No auto-refetch — user controls when to refresh
  endpoints: (builder) => ({
    /**
     * ONE reporting period per cache entry (spec metrics-window-caching R1).
     * The service partitions its cache per period, so a week is the natural
     * cache unit: overlapping windows are ordinary cache hits, two views
     * wanting the same week share one request, and shifting a window fetches
     * only the weeks it gained. Windows are assembled by useMetricsWindow.
     */
    getMetricsWeek: builder.query<GetMetricsOutput, GetMetricsWeekArgs>({
      /**
       * Bounded residency (spec metrics-window-caching R3). One week of real
       * data is ~1,100 DCRs x 35 metric entries, so weeks cannot be retained
       * forever like the other endpoints. RTK collects an entry only once NO
       * component subscribes to it, so a week on screen is never dropped; a
       * week we navigated away from (or merely prefetched) survives ten
       * minutes, which covers normal back-and-forth without unbounded growth.
       */
      keepUnusedDataFor: 600,
      queryFn: async ({ id, dataMode, reportingPeriod }) => {
        try {
          const data = await GetMetricsApi.getMetricsAllPages({
            dashboardId: id,
            reviewType: WBR_REVIEW_TYPE,
            dataMode,
            reportingPeriods: [reportingPeriod],
          });
          return { data };
        } catch (error) {
          if (error instanceof ReportingApiError) {
            return { error: { status: error.status, data: error.body ?? error.errorCode } };
          }
          return { error: { status: 0, data: String(error) } };
        }
      },
    }),
    getMetrics: builder.query<GetMetricsOutput, GetMetricsInput>({
      // Cache key EXCLUDES paginationKey: the accessor returns the
      // accumulated (all-pages) result, stored once per
      // {id, reportingPeriods, dataMode} — components never see partial
      // pages (spec R5.2). Every page requesting the same window shares one
      // entry, so a filter change never refetches.
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { dashboardId, reviewType, reportingPeriods, dataMode } = queryArgs;
        return `${endpointName}(${JSON.stringify({ dashboardId, reviewType, reportingPeriods, dataMode })})`;
      },
      // Delegate transport (incl. the pagination protocol) to the accessor;
      // map its typed error into the RTK error shape.
      queryFn: async (input) => {
        try {
          return { data: await GetMetricsApi.getMetricsAllPages(input) };
        } catch (error) {
          if (error instanceof ReportingApiError) {
            return { error: { status: error.status, data: error.body ?? error.errorCode } };
          }
          return { error: { status: 0, data: String(error) } };
        }
      },
    }),
  }),
});

/**
 * Single entry point for components. Callers may pass `skip` (e.g. while the
 * week universe is still loading).
 */
export function useGetMetricsQuery(input: GetMetricsInput, options?: { skip?: boolean }) {
  return metricsApi.useGetMetricsQuery(input, { skip: options?.skip ?? false });
}
